import {
    useAbortWarehouseDeliveryMutation,
    useArchiveWarehouseDeliveryMutation,
    useBeginWarehouseDeliveryPreparationMutation,
    useFinishWarehouseDeliveryPreparationMutation,
    useMarkWarehouseDeliveryAsDeliveredMutation,
    useMarkWarehouseDeliveryAsInProgressMutation,
    WarehouseDeliveryFragmentFragment,
    DeliveryState,
} from 'data/__generated__';

import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { StatusTag } from './StatusTag';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { Loader, LoaderModeType } from 'components/Loader';
import { confirmPopup } from 'components/Popups/Confirm';

export const StatesSection = ({ warehouseDelivery }: { warehouseDelivery: WarehouseDeliveryFragmentFragment }) => {
    const { _id: warehouseDeliveryId, state, stateHistory } = warehouseDelivery;

    const [markDeliveriesAsAborted, { loading: markAsAbortedLoading }] = useAbortWarehouseDeliveryMutation({
        variables: { warehouseDeliveryId },
    });
    const [markDeliveriesAsArchived, { loading: markAsArchivedLoading }] = useArchiveWarehouseDeliveryMutation({
        variables: { warehouseDeliveryId },
    });
    const [markDeliveriesAsDelivered, { loading: markAsDeliveredLoading }] =
        useMarkWarehouseDeliveryAsDeliveredMutation({
            variables: { warehouseDeliveryId },
        });
    const [markDeliveriesAsInProgress, { loading: markAsInProgressLoading }] =
        useMarkWarehouseDeliveryAsInProgressMutation({ variables: { warehouseDeliveryId } });
    const [markDeliveriesAsPackaging, { loading: markAsPackagingLoading }] =
        useBeginWarehouseDeliveryPreparationMutation({ variables: { warehouseDeliveryId } });
    const [markDeliveriesAsReady, { loading: markAsReadyLoading }] = useFinishWarehouseDeliveryPreparationMutation({
        variables: { warehouseDeliveryId },
    });

    const formattedStateHistory = stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    const STATE_CHANGE_ACTIONS = [
        {
            label: 'Passer en préparation',
            action: markDeliveriesAsPackaging,
            loading: markAsPackagingLoading,
            isDisabled: state !== DeliveryState.Created,
        },
        {
            label: 'Marquer comme prête',
            action: markDeliveriesAsReady,
            loading: markAsReadyLoading,
            isDisabled: state !== DeliveryState.Packaging && state !== DeliveryState.Paused,
        },
        {
            label: 'Passer en livraison',
            action: markDeliveriesAsInProgress,
            loading: markAsInProgressLoading,
            isDisabled: state !== DeliveryState.Ready,
        },
        {
            label: 'Marquer comme livrée',
            action: markDeliveriesAsDelivered,
            loading: markAsDeliveredLoading,
            isDisabled: ![
                DeliveryState.InProgress,
                DeliveryState.Packaging,
                DeliveryState.Ready,
                DeliveryState.Paused,
                DeliveryState.NotDelivered,
            ].includes(state),
        },
        {
            label: 'Annuler la livraison',
            action: markDeliveriesAsAborted,
            loading: markAsAbortedLoading,
            isDisabled: ![
                DeliveryState.Created,
                DeliveryState.InProgress,
                DeliveryState.Packaging,
                DeliveryState.Ready,
                DeliveryState.Paused,
                DeliveryState.NotDelivered,
            ].includes(state),
        },
        {
            label: 'Archiver la livraison',
            action: markDeliveriesAsArchived,
            loading: markAsArchivedLoading,
            isDisabled: ![DeliveryState.Delivered, DeliveryState.Aborted].includes(state),
        },
    ];

    return (
        <SectionContainer title="Historique de statuts" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <StateHistory records={formattedStateHistory} />
                </SectionColumn>
                <SectionColumn>
                    <StatusTag state={state} showHeading />
                </SectionColumn>
                <SectionColumn>
                    {STATE_CHANGE_ACTIONS.map((stateChange, index) => {
                        const { label, action, isDisabled, loading } = stateChange;

                        async function updateState() {
                            const hasConfirmed = await confirmPopup({
                                title: 'Confirmer',
                                message: `Êtes-vous sûr.e de vouloir ${label.toLowerCase()} ?`,
                            });
                            if (hasConfirmed) {
                                await action();
                            }
                        }

                        return (
                            <TotemPrimaryButton key={index} type="button" onClick={updateState} disabled={isDisabled}>
                                {label}
                                {loading ? <Loader size="20px" mode={LoaderModeType.Spin} /> : null}
                            </TotemPrimaryButton>
                        );
                    })}
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};
